import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Markdown from "markdown-to-jsx";
import html2pdf from "html2pdf.js";

import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import { Alert } from "@material-ui/lab";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import { DeleteSweep, PictureAsPdf } from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";

const markdownOptions = {
  disableParsingRawHTML: true,
  overrides: {
    a: {
      component: ({ children, ...props }) => (
        <a {...props} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      )
    }
  }
};
const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(2)
  },
  row: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap"
  },
  field: {
    flex: 1,
    minWidth: 200
  },
  helperText: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary
  },
  chatWindow: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(2),
    backgroundColor: theme.mode === "light" ? "#f7f8fb" : "#232733",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    height: "65vh",
    overflowY: "auto",
    marginBottom: theme.spacing(2)
  },
  msg: {
    maxWidth: "75%",
    padding: theme.spacing(1, 1.5),
    borderRadius: 10,
    fontSize: "0.85rem"
  },
  msgUser: {
    alignSelf: "flex-end",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    whiteSpace: "pre-wrap"
  },
  msgBot: {
    alignSelf: "flex-start",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    "& p": {
      margin: 0
    },
    "& p + p": {
      marginTop: theme.spacing(1)
    },
    "& ul, & ol": {
      margin: theme.spacing(0.5, 0),
      paddingLeft: theme.spacing(2.5)
    },
    "& code": {
      fontFamily: "monospace",
      fontSize: "0.85em",
      backgroundColor: theme.mode === "light" ? "#eef0f4" : "#1a1d26",
      padding: "1px 4px",
      borderRadius: 3
    },
    "& pre": {
      fontFamily: "monospace",
      fontSize: "0.85em",
      backgroundColor: theme.mode === "light" ? "#eef0f4" : "#1a1d26",
      padding: theme.spacing(1),
      borderRadius: 6,
      overflowX: "auto"
    },
    "& a": {
      color: "inherit"
    }
  },
  chatActionBtn: {
    minWidth: 120
  }
}));

// Aba "Agente de I.A." do modal do G-Click, montada por GclickModal. As
// abas voltaram em 2026-10-01 agora que a segunda é real (configuração do
// iframe/ponte REST) -- o motivo de tirá-las antes era que 2 das 3 eram
// mock. `status`/`onStatusChange` vêm do pai pra manter uma única fonte de
// verdade com a tabela/card da página Plataformas -- ver
// PLANO_PLATAFORMAS_GCLICK.md.
const GclickAiAgent = ({ status, onStatusChange, onClose, active = true }) => {
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [providers, setProviders] = useState([]);
  const [gclickClientId, setGclickClientId] = useState("");
  const [gclickClientSecret, setGclickClientSecret] = useState("");

  const [llmProvider, setLlmProvider] = useState("");
  const [llmApiKey, setLlmApiKey] = useState("");
  const [llmModel, setLlmModel] = useState("");
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState("");
  const modelsLoadedForRef = useRef(null);
  const latestRequestRef = useRef(null);
  // Valores do render anterior, para o efeito abaixo saber se a chave mudou
  // de uma tecla ou de um bloco inteiro (colagem / autofill / config).
  const prevApiKeyRef = useRef("");
  const prevProviderRef = useRef("");
  // Modelo salvo, restaurado uma única vez quando a primeira busca de
  // modelos (disparada com o provedor/key já carregados) voltar -- se a key
  // não for mais válida ou o modelo tiver saído da lista, fica em branco
  // pra o usuário escolher de novo, ver maybeLoadModels.
  const pendingModelRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const chatEndRef = useRef(null);

  const hasAiAgentConfig = status?.hasAiAgentConfig ?? false;
  const enabled = status?.enabled ?? false;

  // Libera a segunda linha (Provedor/API Key/Modelo) com a credencial apenas
  // DIGITADA, sem exigir que ela já esteja salva. Antes isto era
  // hasAiAgentConfig, que vem do servidor (getStatus, derivado do que está no
  // banco) -- ou seja, significava "já salvou", e obrigava a salvar, esperar o
  // round-trip, preencher a segunda linha e salvar de novo. O backend sempre
  // aceitou os cinco campos num único PUT; a trava era só daqui.
  const hasCredentials = Boolean(
    gclickClientId.trim() && gclickClientSecret.trim()
  );

  // `active` nas deps porque o painel inativo sai com display:none (ver
  // GclickModal) e aí o chat não tem dimensão -- o scrollIntoView vira no-op
  // e a conversa abriria no topo. Rola de novo quando a aba fica visível.
  useEffect(() => {
    if (!active) return;
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, chatLoading, active]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [providersRes, configRes, messagesRes] = await Promise.all([
          api.get("/llm-providers"),
          api.get("/gclickSettings/ai-agent"),
          api.get("/gclickSettings/ai-agent/messages")
        ]);
        setProviders(providersRes.data);
        setGclickClientId(configRes.data.gclickClientId || "");
        setGclickClientSecret(configRes.data.gclickClientSecret || "");
        setLlmProvider(configRes.data.llmProvider || "");
        setLlmApiKey(configRes.data.llmApiKey || "");
        pendingModelRef.current = configRes.data.llmModel || null;
        setMessages(messagesRes.data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/gclickSettings/ai-agent", {
        gclickClientId,
        gclickClientSecret,
        llmProvider,
        llmApiKey,
        llmModel
      });
      toast.success("Configurações salvas com sucesso.");
      // A regra de quando isso ativa sozinho (transição não-configurado ->
      // configurado) vive só no backend (updateAiAgentConfig) -- reconsulta
      // o status real em vez de recalcular a mesma regra aqui, pra não ter
      // duas cópias da lógica que podem sair de sincronia.
      const { data } = await api.get("/gclickSettings/status");
      onStatusChange({ ...status, ...data });
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  // Modelo depende de Provedor + API Key -- busca ao vivo em vez de lista
  // fixa. Sem filtro por provedor de propósito, ver PLANO_PLATAFORMAS_GCLICK.md.
  //
  // latestRequestRef guarda qual combinação provedor+key é a mais recente
  // pedida -- sem isso, trocar de provedor rápido (ex: A, depois B, antes da
  // resposta de A voltar) deixa a resposta de A, se chegar depois da de B,
  // sobrescrever o dropdown com modelos de um provedor que não é mais o
  // selecionado. Só aplica a resposta se ainda for a mais recente.
  const maybeLoadModels = async () => {
    // Sem hasAiAgentConfig/hasCredentials aqui de propósito: buscar modelos
    // chama /llm-providers/:id/models com a chave da I.A., que não tem
    // relação nenhuma com a credencial do G-Click. Deixar a credencial do
    // G-Click nesta condição faria o ramo abaixo rodar (e apagar o modelo já
    // escolhido) só porque o usuário voltou pra editar o Client ID. Quem
    // bloqueia visualmente são os `disabled` dos campos.
    if (!llmProvider || !llmApiKey) {
      modelsLoadedForRef.current = null;
      latestRequestRef.current = null;
      setModels([]);
      setLlmModel("");
      setModelsError("");
      return;
    }

    const comboKey = `${llmProvider}|${llmApiKey}`;
    if (modelsLoadedForRef.current === comboKey) return;

    latestRequestRef.current = comboKey;
    setModelsLoading(true);
    setModelsError("");
    setModels([]);
    setLlmModel("");
    try {
      const { data } = await api.post(`/llm-providers/${llmProvider}/models`, {
        apiKey: llmApiKey
      });
      if (latestRequestRef.current !== comboKey) return; // resposta obsoleta
      setModels(data);
      modelsLoadedForRef.current = comboKey;
      if (pendingModelRef.current && data.some(m => m.id === pendingModelRef.current)) {
        setLlmModel(pendingModelRef.current);
      }
      pendingModelRef.current = null;
    } catch (err) {
      if (latestRequestRef.current !== comboKey) return;
      setModelsError(
        err?.response?.data?.error === "ERR_LLM_INVALID_KEY"
          ? "Chave inválida ou sem acesso a esse provedor."
          : "Não foi possível buscar os modelos desse provedor agora."
      );
    } finally {
      if (latestRequestRef.current === comboKey) setModelsLoading(false);
    }
  };

  // Busca os modelos quando o provedor muda, ou quando a chave entra de uma
  // vez -- colagem, autofill, ou a config chegando ao abrir a aba.
  //
  // Antes o único gatilho pela chave era o onBlur, então colar a chave e
  // continuar no campo não carregava nada; e ao REABRIR o modal o efeito
  // rodava com a chave ainda vazia e nunca mais, porque llmApiKey não era
  // dependência (este React, 16.13 com ReactDOM.render, não agrupa setState
  // fora de event handler, então setLlmProvider renderiza antes de
  // setLlmApiKey). Observar llmApiKey conserta os dois.
  //
  // Mas observar a chave significa um render por tecla, e cada tentativa é
  // uma chamada de verdade à API do provedor. O salto de tamanho separa os
  // dois casos: colagem, autofill e carga da config chegam num único evento,
  // com o valor inteiro; digitação anda de um caractere por vez. Medido:
  // digitar a chave à mão com pausa a cada tecla dispara 49 requisições, 48
  // com chave parcial, num debounce de 700ms -- e zero aqui. Quem digita à
  // mão carrega ao sair do campo, pelo onBlur, como sempre foi.
  //
  // Sem timer de propósito: nada fica pendente, então o onBlur não tem com o
  // que colidir e o modelo salvo volta na hora ao abrir, sem espera.
  useEffect(() => {
    const keyJump = llmApiKey.length - prevApiKeyRef.current.length;
    const providerChanged = llmProvider !== prevProviderRef.current;
    prevApiKeyRef.current = llmApiKey;
    prevProviderRef.current = llmProvider;

    if (providerChanged || Math.abs(keyJump) > 1) {
      maybeLoadModels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llmProvider, llmApiKey]);

  const canSendChat = enabled && llmProvider && llmModel && llmApiKey && chatInput.trim() && !chatLoading;

  const handleSend = async () => {
    const content = chatInput.trim();
    // Otimista: aparece na tela na hora, mas o backend é quem de fato
    // decide o que persiste -- ele salva a mensagem do usuário antes de
    // chamar o LLM, então mesmo se a chamada abaixo falhar essa mensagem já
    // não se perde (ver GclickSettingsController.chatWithAiAgent).
    setMessages(prev => [...prev, { role: "user", content }]);
    setChatInput("");
    setChatLoading(true);
    try {
      const { data } = await api.post("/gclickSettings/ai-agent/chat", {
        llmProvider,
        llmModel,
        llmApiKey,
        content
      });
      setMessages(prev => [...prev, { role: "assistant", content: data.reply }]);
    } catch (err) {
      toastError(err);
    } finally {
      setChatLoading(false);
    }
  };

  const handleClearConversation = async () => {
    try {
      await api.delete("/gclickSettings/ai-agent/messages");
      setMessages([]);
    } catch (err) {
      toastError(err);
    }
  };

  // Só limpa o rascunho não enviado -- diferente de handleClearConversation
  // acima (Limpar conversa, apaga o histórico salvo).
  const handleClearDraft = () => setChatInput("");

  // Diferente da exportação de tickets (MessagesList/#messagesList), cujo
  // height vem de flexGrow do pai e some ao clonar isolado -- aqui o
  // chatWindow declara height/overflow direto na própria classe, então o
  // clone herda o mesmo recorte de tela (só a fatia rolada visível) se não
  // for sobrescrito. Sem isso o PDF sairia cortado na altura da janela, não
  // com a conversa inteira.
  const handleExportToPDF = () => {
    const chatWindowElement = document.getElementById("gclickChatWindow");
    if (!chatWindowElement) return;

    const chatWindowClone = chatWindowElement.cloneNode(true);
    chatWindowClone.style.height = "auto";
    chatWindowClone.style.maxHeight = "none";
    chatWindowClone.style.overflow = "visible";

    const titleElement = document.createElement("h2");
    titleElement.style.textAlign = "center";
    titleElement.style.margin = "0 0 16px 0";
    titleElement.innerText = "Histórico de Conversa — Agente de I.A. G-Click";

    const dateElement = document.createElement("p");
    dateElement.style.textAlign = "center";
    dateElement.style.margin = "0 0 16px 0";
    dateElement.innerText = `Exportado em ${new Date().toLocaleString("pt-BR")}`;

    const containerElement = document.createElement("div");
    containerElement.appendChild(titleElement);
    containerElement.appendChild(dateElement);
    containerElement.appendChild(chatWindowClone);

    const pdfOptions = {
      margin: 1,
      filename: `conversa_agente_ia_gclick_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }
    };

    html2pdf().from(containerElement).set(pdfOptions).save();
  };

  if (loading) {
    return <Typography>Carregando...</Typography>;
  }

  return (
    <div>
      <Box className={`${classes.row} ${classes.section}`}>
        <TextField
          variant="outlined"
          size="small"
          label="G-Click Client ID"
          className={classes.field}
          value={gclickClientId}
          onChange={e => setGclickClientId(e.target.value)}
          autoComplete="off"
        />
        <TextField
          variant="outlined"
          size="small"
          type="password"
          label="G-Click Client Secret"
          className={classes.field}
          value={gclickClientSecret}
          onChange={e => setGclickClientSecret(e.target.value)}
          autoComplete="new-password"
        />
      </Box>

      <Box className={`${classes.row} ${classes.section}`}>
        <FormControl
          variant="outlined"
          size="small"
          className={classes.field}
          disabled={!hasCredentials}
        >
          <InputLabel>Provedor</InputLabel>
          <Select
            label="Provedor"
            value={llmProvider}
            onChange={e => setLlmProvider(e.target.value)}
          >
            {providers.map(provider => (
              <MenuItem key={provider.id} value={provider.id}>
                {provider.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          variant="outlined"
          size="small"
          type="password"
          label="API Key da I.A."
          placeholder="sk-..."
          className={classes.field}
          value={llmApiKey}
          disabled={!hasCredentials}
          onChange={e => setLlmApiKey(e.target.value)}
          onBlur={maybeLoadModels}
          autoComplete="new-password"
        />
        <Autocomplete
          size="small"
          className={classes.field}
          disabled={!hasCredentials || !llmProvider || !llmApiKey || modelsLoading || models.length === 0}
          options={models}
          getOptionLabel={option => option.name}
          getOptionSelected={(option, value) => option.id === value.id}
          value={models.find(model => model.id === llmModel) || null}
          onChange={(e, value) => setLlmModel(value ? value.id : "")}
          renderInput={params => (
            <TextField {...params} label="Modelo" variant="outlined" placeholder="Buscar modelo..." />
          )}
        />
      </Box>
      {modelsLoading && (
        <Typography className={`${classes.helperText} ${classes.section}`}>
          Carregando modelos...
        </Typography>
      )}
      {modelsError && (
        <Alert severity="error" className={classes.section}>
          {modelsError}
        </Alert>
      )}

      <Box className={classes.section}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveSettings}
          disabled={saving}
        >
          Salvar configurações
        </Button>
      </Box>

      <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
        <Button
          size="small"
          startIcon={<PictureAsPdf fontSize="small" />}
          disabled={messages.length === 0}
          onClick={handleExportToPDF}
        >
          Exportar PDF
        </Button>
        <Button
          size="small"
          startIcon={<DeleteSweep fontSize="small" />}
          disabled={messages.length === 0}
          onClick={() => setClearConfirmOpen(true)}
        >
          Limpar conversa
        </Button>
      </Box>

      <ConfirmationModal
        title="Limpar conversa?"
        open={clearConfirmOpen}
        onClose={() => setClearConfirmOpen(false)}
        onConfirm={handleClearConversation}
      >
        Todo o histórico dessa conversa com o Agente de I.A. será apagado. Essa ação não
        pode ser desfeita.
      </ConfirmationModal>

      <div id="gclickChatWindow" className={classes.chatWindow}>
        {messages.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            Nenhuma mensagem ainda. Pergunte algo sobre um cliente do G-Click.
          </Typography>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`${classes.msg} ${m.role === "user" ? classes.msgUser : classes.msgBot}`}
          >
            {m.role === "user" ? (
              m.content
            ) : (
              <Markdown options={markdownOptions}>{m.content}</Markdown>
            )}
          </div>
        ))}
        {chatLoading && (
          <div className={`${classes.msg} ${classes.msgBot}`}>Pensando...</div>
        )}
        <div ref={chatEndRef} />
      </div>

      <Box className={classes.section}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="mensagem"
          fullWidth
          multiline
          maxRows={5}
          value={chatInput}
          onChange={e => setChatInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (canSendChat) handleSend();
            }
          }}
          disabled={!enabled || chatLoading}
        />
        <Typography className={classes.helperText} style={{ marginTop: 4 }}>
          Enter envia · Shift+Enter quebra linha
        </Typography>
        <Box display="flex" justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
          <Button
            variant="outlined"
            color="secondary"
            className={classes.chatActionBtn}
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            variant="outlined"
            className={classes.chatActionBtn}
            style={{ borderColor: "#f9a825", color: "#f9a825" }}
            onClick={handleClearDraft}
            disabled={!chatInput}
          >
            Limpar
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.chatActionBtn}
            onClick={handleSend}
            disabled={!canSendChat}
          >
            Enviar
          </Button>
        </Box>
      </Box>
      {!enabled && (
        <Typography className={classes.helperText}>
          {hasAiAgentConfig
            ? "O acesso da sua empresa ao G-Click está bloqueado. Fale com o administrador do sistema."
            : "Salve as credenciais para começar a conversar."}
        </Typography>
      )}
    </div>
  );
};

export default GclickAiAgent;
